import { isAtLeast } from '../utils/_breakpoints.js';

class oFaqGrid extends HTMLDivElement {
    constructor() {
        super();
        this.footerAnchors = [];
        this.tabpanelView = null;
        this.tablistNode = null;
        this.tabs = [];
        this.tabpanels = [];
        this.firstTab = null;
        this.lastTab = null;
    }

    connectedCallback() {
        this.footerAnchors = document.querySelectorAll('[is="m-footer-block"].assistance a');
        this.tabpanelView = this.querySelector('.faq-main');
        this.tablistNode = this.querySelector('[role=tablist]');

        // Setup tabs and tabpanels
        let currentTab = null;
        this.tabs = Array.from(this.tablistNode.querySelectorAll('[role=tab]'));
        this.tabs.forEach((tab) => {
            const tabpanel = document.getElementById(tab.getAttribute('aria-controls'));
            tab.tabIndex = -1;
            tab.setAttribute('aria-selected', 'false');
            this.tabpanels.push(tabpanel);
            tab.addEventListener('keydown', this.onTabKeydown.bind(this));
            tab.addEventListener('click', this.onTabClick.bind(this));

            if (!this.firstTab && !tab.classList.contains('is-hidden')) {
                this.firstTab = tab;
            } else {
                this.lastTab = tab;
            }

            // Set already selected tab according to URL hash
            const currentUrlHash = window.location.hash;
            const hrefHash = new URL(tab.href).hash;
            if (currentUrlHash && hrefHash && currentUrlHash === hrefHash) {
                currentTab = tab;
            }
        });
        if (currentTab) {
            this.setSelectedTab(currentTab);
        } else {
            this.displayDefaultTab();
        }

        // Setup history event callback
        window.addEventListener('popstate', () => {
            const tab = this.tabs.find(t => t.hash === window.location.hash);
            this.setSelectedTab.call(this, tab);
        });

        // Modify FAQ links on footer
        this.footerAnchors.forEach(a => {
            a.addEventListener('click', this.onFooterAnchorClick.bind(this));
        });
    }

    updateDefaultTabVisible() {
        this.dataset.defaultTabVisible = this.tabs[0].getAttribute('aria-selected') === 'true';
    }

    updateHistory(anchorElem) {
        if (!anchorElem || !anchorElem.hash) {
            window.history.pushState(null, null, ' ');
        } else if (anchorElem.href !== location.href) {
            window.history.pushState(null, null, anchorElem.hash);
        }
    }

    displayDefaultTab() {
        this.setSelectedTab(this.tabs[0]);
    }

    displaySearchTab() {
        this.setSelectedTab(this.tabs[1]);
    }

    scrollToContent() {
        if (isAtLeast('md')) {
            // Desktop: scroll to top
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            // Mobile: scroll to tabpanel
            const yOffset = -200; // Avoid sticking the tabpanel at the tippy top of the screen
            const top = this.tabpanelView.getBoundingClientRect().top + window.pageYOffset + yOffset;
            window.scrollTo({ top, behavior: 'smooth' });
        }
        // Mostly for a11y purpose, but can correct bad scroll if out of view
        setTimeout(() => {
            this.tabpanelView.focus();
        }, 300);
    }

    setSelectedTab(currentTab) {
        let visibleTabSelected = null;
        this.tabs.forEach((tab, index) => {
            if (currentTab === tab) {
                if (!tab.classList.contains('is-hidden')) {
                    visibleTabSelected = tab;
                    tab.removeAttribute('tabindex');
                }
                tab.setAttribute('aria-selected', 'true');
                tab.classList.add('active');
                this.tabpanels[index].classList.remove('is-hidden');
            } else {
                tab.setAttribute('aria-selected', 'false');
                tab.tabIndex = -1;
                tab.classList.remove('active');
                this.tabpanels[index].classList.add('is-hidden');
            }
        });
        if (!visibleTabSelected) {
            // No visible tab selected ; we allow the first tab to be focusable
            const firstVisibleTab = this.tabs.find(t => !t.classList.contains('is-hidden'));
            if (firstVisibleTab) {
                firstVisibleTab.removeAttribute('tabindex');
            }
        }

        this.updateHistory(currentTab);
        this.updateDefaultTabVisible();
        this.scrollToContent();
    }

    moveFocusToTab(currentTab) {
        currentTab.focus();
    }

    moveFocusToPreviousTab(currentTab) {
        if (currentTab === this.firstTab) {
            this.moveFocusToTab(this.lastTab);
        } else {
            const index = this.tabs.indexOf(currentTab);
            this.moveFocusToTab(this.tabs[index - 1]);
        }
    }

    moveFocusToNextTab(currentTab) {
        if (currentTab === this.lastTab) {
            this.moveFocusToTab(this.firstTab);
        } else {
            const index = this.tabs.indexOf(currentTab);
            this.moveFocusToTab(this.tabs[index + 1]);
        }
    }

    onTabKeydown(event) {
        const target = event.currentTarget;
        let flag = false;

        switch (event.key) {
            case 'ArrowLeft':
                this.moveFocusToPreviousTab(target);
                flag = true;
                break;

            case 'ArrowRight':
                this.moveFocusToNextTab(target);
                flag = true;
                break;

            case 'Home':
                this.moveFocusToTab(this.firstTab);
                flag = true;
                break;

            case 'End':
                this.moveFocusToTab(this.lastTab);
                flag = true;
                break;

            default:
                break;
        }

        if (flag) {
            event.stopPropagation();
            event.preventDefault();
        }
    }

    onTabClick(event) {
        event.preventDefault(); // Prevent anchor auto-scroll but we need to setup URL history
        const tab = event.currentTarget;
        if (tab.classList.contains('active')) {
            // Unselect tab if tab is current active tab
            this.displayDefaultTab();
        } else {
            this.setSelectedTab(tab);
        }
    }

    onFooterAnchorClick(event) {
        const anchor = event.currentTarget;
        const tab = this.tabs.find(t => t.href === anchor.href);
        if (tab) {
            event.preventDefault(); // Prevent anchor auto-scroll but we need to setup URL history
            this.setSelectedTab(tab);
        }
    }
}

customElements.define('o-faq-grid', oFaqGrid, {
    extends: 'div'
});
